class NeuracoilStickyStoryComponent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.video = this.querySelector('.neuracoil-sticky-story__video');
    this.accessoryStoryRoot = this.closest('.neuracoil-accessory-story');
    this.frameFallback = this.querySelector('[data-story-frame-fallback]');
    this.mediaColumn = this.querySelector('.neuracoil-sticky-story__media-column');
    this.mediaSpacer = this.querySelector('[data-story-media-spacer]');
    this.releaseSlot = this.querySelector('[data-story-release-slot]');
    this.stickyMedia = this.querySelector('[data-story-media-sticky]');
    this.layout = this.querySelector('.neuracoil-sticky-story__layout');
    this.stepsTrack = this.querySelector('[data-story-steps]');
    this.steps = Array.from(this.querySelectorAll('[data-story-step]'));
    this.mobileCopyStage = this.querySelector('[data-story-mobile-copy-stage]');
    this.mobilePanels = Array.from(this.querySelectorAll('[data-story-mobile-panel]'));
    this.progressItems = Array.from(this.querySelectorAll('[data-story-progress-item]'));
    this.currentChapter = this.querySelector('[data-story-current-chapter]');
    this.header = document.querySelector('#header-component');
    this.originalMediaParent = this.mediaColumn?.parentNode ?? null;
    this.originalMediaNextSibling = this.mediaColumn?.nextSibling ?? null;
    this.iosPortal = null;
    this.mediaIsPortaled = false;
    this.mediaQuery = window.matchMedia('(max-width: 989px)');
    this.mobileScrubQuery = window.matchMedia('(max-width: 749px)');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const searchParams = new URLSearchParams(window.location.search);
    this.forceIOSMode = searchParams.has('nc_story_ios_mode');
    this.forceIOSBrowserShell = searchParams.has('nc_story_ios_shell');
    this.isIOS = this._detectIOS();
    this.isIOSBrowserShell = this._detectIOSBrowserShell();
    this.preferIOSFrameFallback = this.hasAttribute('data-story-ios-frame-fallback');
    this.storyFrameCount = Number.parseInt(this.dataset.storyFrameCount, 10) || 0;
    this.storyFrameStep = Number.parseInt(this.dataset.storyFrameStep, 10) || 30;
    this.storyFrameSpriteCols = Number.parseInt(this.dataset.storyFrameSpriteCols, 10) || 5;
    this.storyFrameSpriteRows = Number.parseInt(this.dataset.storyFrameSpriteRows, 10) || 5;
    this.storyFrameSpriteSize = Number.parseInt(this.dataset.storyFrameSpriteSize, 10) || this.storyFrameSpriteCols * this.storyFrameSpriteRows;
    this.storyFrameSprites = this._getFrameFallbackSprites();
    this.frameFallbackPreloads = [];
    this.frameFallbackLoadPromise = null;
    this.frameFallbackReady = false;
    this.frameFallbackFailed = false;
    this.chapterHoldRatio = Number.parseFloat(this.dataset.storyChapterHoldRatio) || 0.18;
    this.storyVariant = this.dataset.storyVariant || 'standard';
    this.storyScrubProfile = this.dataset.storyScrubProfile || 'chapters';
    this.storyChapterFrames = (this.dataset.storyChapterFrames || '')
      .split(',')
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter((value) => Number.isFinite(value));
    this.storyStartOffsetRatio = Math.min(
      Math.max(Number.parseFloat(this.dataset.storyStartOffsetRatio) || 0, 0),
      0.35
    );
    this.activeIndex = -1;
    this.videoDuration = 0;
    this.targetTime = 0;
    this.targetDirection = 0;
    this.frame = null;
    this.scrubFrame = null;
    this.pendingForce = false;
    this.isStoryVisible = false;
    this.visibilityObserver = null;
    this.preloadObserver = null;
    this.videoLoadRequested = false;
    this.lastSeekAt = 0;
    this.seekStartedAt = 0;
    this.lastSeekRecoveryAt = 0;
    this.seekOnlyPendingTime = null;
    this.lastSeekOnlyFrame = -1;
    this.lastFrameFallbackFrame = -1;
    this.lastFrameFallbackSprite = -1;
    this.lastEventScrollY = window.scrollY || 0;
    this.lastSyncScrollY = this.lastEventScrollY;
    this.videoPaintPrimed = false;
    this.videoPaintPrimeAttempted = false;
    this.videoPaintPrimeRetryAt = 0;
    this.stableViewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    this.lastViewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    this.scrollDirection = 0;
    this.releaseHysteresisPx = this.isIOS ? 44 : 18;
    this.iosPortalGeometryDirty = true;
    this.iosPortalTop = 0;
    this.iosPortalHeight = 0;
    this.iosPortalViewportFrame = null;

    // Layout cache — recomputed only on resize / breakpoint change / metadata
    // load instead of every scroll frame. Avoids forced sync layout from
    // getComputedStyle() + getBoundingClientRect() on every rAF tick.
    this._cache = {
      anchorY: 0,
      stickyTop: 0,
      stickyHeight: 0,
      pinStartY: 0,
      pinEndY: 0,
      isMobile: false,
      dirty: true,
    };

    if (!(this.video instanceof HTMLMediaElement) || !(this.stepsTrack instanceof HTMLElement) || this.steps.length === 0) {
      return;
    }

    this._onResize = this._onResize.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onBreakpoint = this._onBreakpoint.bind(this);
    this._onLoadedMetadata = this._onLoadedMetadata.bind(this);
    this._onLoadedData = this._onLoadedData.bind(this);
    this._onCanPlay = this._onCanPlay.bind(this);
    this._onSeeked = this._onSeeked.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
    this._onPreloadIntersection = this._onPreloadIntersection.bind(this);
    this._onStoryIntersection = this._onStoryIntersection.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onMotionPreferenceChange = this._onMotionPreferenceChange.bind(this);
    this._onVisualViewportChange = this._onVisualViewportChange.bind(this);
    this._tickScrub = this._tickScrub.bind(this);

    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.volume = 0;
    this.video.setAttribute('muted', '');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');
    this.video.preload = 'metadata';
    this.video.setAttribute('preload', 'metadata');
    this.video.loop = false;
    this.video.pause();
    this._normalizeConfiguredSources();
    this._selectVideoSource(true, false);
    this._updatePinModeClass();
    this._updateBrowserShellClass();

    if (this.video.readyState >= 1 && Number.isFinite(this.video.duration)) {
      this._setVideoDuration();
    }

    this.video.addEventListener('loadedmetadata', this._onLoadedMetadata);
    this.video.addEventListener('loadeddata', this._onLoadedData);
    this.video.addEventListener('canplay', this._onCanPlay);
    this.video.addEventListener('seeked', this._onSeeked);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('wheel', this._onWheel, { passive: true });
    window.visualViewport?.addEventListener('resize', this._onVisualViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', this._onVisualViewportChange, { passive: true });
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this._onBreakpoint);
    } else {
      this.mediaQuery.addListener(this._onBreakpoint);
    }

    if (typeof this.mobileScrubQuery.addEventListener === 'function') {
      this.mobileScrubQuery.addEventListener('change', this._onBreakpoint);
    } else {
      this.mobileScrubQuery.addListener(this._onBreakpoint);
    }

    if (typeof this.reduceMotionQuery.addEventListener === 'function') {
      this.reduceMotionQuery.addEventListener('change', this._onMotionPreferenceChange);
    } else {
      this.reduceMotionQuery.addListener(this._onMotionPreferenceChange);
    }

    document.addEventListener('shopify:block:select', this._onBlockSelect);

    if ('IntersectionObserver' in window) {
      this.preloadObserver = new IntersectionObserver(this._onPreloadIntersection, {
        rootMargin: '1200px 0px 1200px 0px',
        threshold: 0,
      });
      this.preloadObserver.observe(this);

      this.visibilityObserver = new IntersectionObserver(this._onStoryIntersection, {
        rootMargin: '-12% 0px -12% 0px',
        threshold: 0.1,
      });
      this.visibilityObserver.observe(this);
    } else {
      this._prepareFrameFallback();
      this._selectVideoSource(false, true);
      this.isStoryVisible = true;
      this._updateVideoPlayback();
    }

    this._sync(true);
    this._onVisualViewportChange();
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.video?.removeEventListener('loadedmetadata', this._onLoadedMetadata);
    this.video?.removeEventListener('loadeddata', this._onLoadedData);
    this.video?.removeEventListener('canplay', this._onCanPlay);
    this.video?.removeEventListener('seeked', this._onSeeked);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('wheel', this._onWheel);
    window.visualViewport?.removeEventListener('resize', this._onVisualViewportChange);
    window.visualViewport?.removeEventListener('scroll', this._onVisualViewportChange);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);

    if (typeof this.mediaQuery?.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this._onBreakpoint);
    } else {
      this.mediaQuery?.removeListener(this._onBreakpoint);
    }

    if (typeof this.mobileScrubQuery?.removeEventListener === 'function') {
      this.mobileScrubQuery.removeEventListener('change', this._onBreakpoint);
    } else {
      this.mobileScrubQuery?.removeListener(this._onBreakpoint);
    }

    if (typeof this.reduceMotionQuery?.removeEventListener === 'function') {
      this.reduceMotionQuery.removeEventListener('change', this._onMotionPreferenceChange);
    } else {
      this.reduceMotionQuery?.removeListener(this._onMotionPreferenceChange);
    }

    document.removeEventListener('shopify:block:select', this._onBlockSelect);
    this.preloadObserver?.disconnect();
    this.preloadObserver = null;
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    this.video?.pause();
    this._clearIOSPinState({ removeMode: true });
    this.mediaColumn?.style.removeProperty('--story-handoff-opacity');

    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    if (this.scrubFrame !== null) {
      cancelAnimationFrame(this.scrubFrame);
      this.scrubFrame = null;
    }

    if (this.iosPortalViewportFrame !== null) {
      cancelAnimationFrame(this.iosPortalViewportFrame);
      this.iosPortalViewportFrame = null;
    }

    this.initialized = false;
  }

  _onResize() {
    const nextWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const nextHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (this.mediaQuery.matches && Math.abs(nextWidth - this.lastViewportWidth) < 2) {
      this._onVisualViewportChange();
      return;
    }

    this.lastViewportWidth = nextWidth;
    this.stableViewportHeight = nextHeight || this.stableViewportHeight;
    this.iosPortalGeometryDirty = true;
    this._cache.dirty = true;
    this._scheduleSync(true);
  }

  _onScroll() {
    const nextScrollY = window.scrollY || 0;

    // Stop forward decoding on the native scroll event, before the next
    // animation-frame sync calculates a backward MirrorCoil target. This
    // avoids a one-frame forward flash when entering and immediately leaving
    // the story with a fast wheel gesture.
    if (
      this.storyScrubProfile === 'mirrorcoil-continuous' &&
      nextScrollY < this.lastEventScrollY &&
      this.video instanceof HTMLMediaElement
    ) {
      this.video.pause();
      this.video.playbackRate = 1;
    }

    this.lastEventScrollY = nextScrollY;
    this._scheduleSync(false);
  }

  _onWheel(event) {
    if (
      this.storyScrubProfile === 'mirrorcoil-continuous' &&
      event.deltaY < 0 &&
      this.video instanceof HTMLMediaElement
    ) {
      this.video.pause();
      this.video.playbackRate = 1;
    }
  }

  _onVisualViewportChange() {
    if (!this.mobileScrubQuery.matches) {
      this.mediaColumn?.style.removeProperty('--story-visual-bottom-inset');
      this.mediaColumn?.style.removeProperty('--story-handoff-opacity');
      return;
    }

    // Keep scroll distance, copy sizing and chapter selection on the stable
    // svh-based layout. Only the visual chrome follows the browser viewport
    // while the mobile URL bar expands or retracts.
    const nextWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    if (Math.abs(nextWidth - this.lastViewportWidth) >= 2) {
      this._onResize();
      return;
    }

    if (this.iosPortalViewportFrame !== null) return;

    this.iosPortalViewportFrame = requestAnimationFrame(() => {
      this.iosPortalViewportFrame = null;
      this._syncMobileVisualViewport();
      this._scheduleSync(false);
    });
  }

  _onBreakpoint() {
    this._cache.dirty = true;
    this.iosPortalGeometryDirty = true;
    this._selectVideoSource(false, this.videoLoadRequested || this.isStoryVisible);
    this._prepareFrameFallback();
    this._syncFrameFallbackReadyClass();
    this._updatePinModeClass();
    this._cancelScrubLoop();
    this._scheduleSync(true);
    this._updateVideoPlayback();
    this._onVisualViewportChange();
  }

  _onLoadedMetadata() {
    this._setVideoDuration();
    this._cache.dirty = true;
    this._scheduleSync(true);
    this._updateVideoPlayback();
  }

  _onLoadedData() {
    this._primeIOSBrowserShellPaint();
    this._updateVideoPlayback();
  }

  _onCanPlay() {
    this._primeIOSBrowserShellPaint();
    this._updateVideoPlayback();
  }

  _onSeeked() {
    this.seekStartedAt = 0;
    const usesQueuedPausedSeek = this._usesSeekOnlyScrub() || this._usesFrameAccurateContinuousScrub();
    if (!usesQueuedPausedSeek || this.seekOnlyPendingTime === null) return;

    this._ensureScrubLoop();
  }

  _onBlockSelect(event) {
    const block = event.target instanceof Element ? event.target.closest('[data-story-step]') : null;

    if (!(block instanceof HTMLElement) || !this.contains(block)) return;

    block.scrollIntoView({
      behavior: 'smooth',
      block: this.isMobileView() ? 'start' : 'center',
    });
  }

  _onPreloadIntersection(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this._prepareFrameFallback();
    this._selectVideoSource(false, true);
    this.preloadObserver?.disconnect();
    this.preloadObserver = null;
  }

  _onStoryIntersection(entries) {
    const entry = entries[entries.length - 1];
    this.isStoryVisible = Boolean(entry?.isIntersecting);
    if (this.isStoryVisible) {
      this._prepareFrameFallback();
      this._selectVideoSource(false, true);
    }
    this._updateVideoPlayback();
  }

  _onVisibilityChange() {
    this._updateVideoPlayback();
  }

  _onMotionPreferenceChange() {
    this._updateVideoPlayback();
  }

  _setVideoDuration() {
    if (!(this.video instanceof HTMLMediaElement) || !Number.isFinite(this.video.duration)) return;
    this.videoDuration = Math.max(this.video.duration - 0.033, 0);
  }

  _getFallbackTimelineDuration() {
    const lastFrame = Math.max(this.storyFrameCount - 1, 0);

    return lastFrame > 0 ? lastFrame / 30 : 0;
  }

  _getScrubDuration() {
    // Browser-shell iOS uses sprite frames; keep that timeline stable even
    // after video metadata arrives so frames do not remap mid-scroll.
    if (this._usesFrameFallback()) return this._getFallbackTimelineDuration();

    return this.videoDuration > 0 ? this.videoDuration : 0;
  }

  _detectIOS() {
    const platform = navigator.platform || '';
    const userAgent = navigator.userAgent || '';

    return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  _detectIOSBrowserShell() {
    if (this.forceIOSBrowserShell) return true;

    const userAgent = navigator.userAgent || '';
    // Firefox for Android exposes the MP4 metadata but does not reliably paint
    // frames while a paused video is seeked by scroll. Use the existing sprite
    // timeline there as well; it is smaller than the MP4 and paints immediately.
    if (/\bAndroid\b/.test(userAgent) && /\bFirefox\/\d+/.test(userAgent)) return true;
    if (!this.isIOS) return false;

    return /\b(CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|GSA)\b/.test(userAgent);
  }

  _usesIOSPinMode() {
    return (this.isIOS || this.forceIOSMode) && this.mobileScrubQuery.matches;
  }

  _updatePinModeClass() {
    this.classList.toggle('is-ios-fixed-pin', this._usesIOSPinMode());
    this.classList.remove('is-ios-transform-pin');

    if (!this._usesIOSPinMode()) {
      this._clearIOSPinState({ removeMode: true });
    }
  }

  _updateBrowserShellClass() {
    this.classList.toggle('is-ios-browser-shell', this.isIOSBrowserShell);

    if (this.iosPortal instanceof HTMLElement) {
      this.iosPortal.classList.toggle('is-ios-browser-shell-portal', this.isIOSBrowserShell);
    }

    this._syncFrameFallbackReadyClass();
  }

  _syncFrameFallbackReadyClass() {
    const isReady =
      this.frameFallbackReady &&
      !this.frameFallbackFailed &&
      this._shouldUseIOSFrameFallback() &&
      this.mobileScrubQuery.matches;

    this.classList.toggle('is-frame-fallback-ready', isReady);
    this.iosPortal?.classList.toggle('is-frame-fallback-ready', isReady);
  }

  _normalizeConfiguredSources() {
    if (!(this.video instanceof HTMLMediaElement)) return;

    const normalizedMobileSource = this._normalizeMobileVideoUrl(this.video.dataset.mobileSrc);
    if (normalizedMobileSource && normalizedMobileSource !== this.video.dataset.mobileSrc) {
      this.video.dataset.mobileSrc = normalizedMobileSource;
    }

    const normalizedIOSSource = this._normalizeMobileVideoUrl(this.video.dataset.iosSrc);
    if (normalizedIOSSource && normalizedIOSSource !== this.video.dataset.iosSrc) {
      this.video.dataset.iosSrc = normalizedIOSSource;
    }

    this.video.querySelectorAll('source[media]').forEach((source) => {
      const media = source.getAttribute('media') || '';
      if (!media.includes('749px')) return;

      const normalizedSource = this._normalizeMobileVideoUrl(source.getAttribute('src'));
      if (normalizedSource) source.setAttribute('src', normalizedSource);
    });
  }

  _normalizeMobileVideoUrl(source) {
    if (!source) return source;

    const staleMobileVideoPattern =
      /explosion_guitar_mobile_(?:ramp_720x480|720x480|current_720x480)_300samples_(?:anim_)?090-210\.mp4/;

    if (!staleMobileVideoPattern.test(source)) return source;

    const [assetPath] = source.split('?');
    return `${assetPath.replace(
      staleMobileVideoPattern,
      'mirrorcoil-story-mobile-9x16-v4-bottomfill-balanced-720x1280-32s-gop6.mp4'
    )}?v=1893`;
  }

  _selectVideoSource(force = false, shouldLoad = this.videoLoadRequested) {
    if (!(this.video instanceof HTMLMediaElement)) return;

    const usesFrameFallback = this._usesFrameFallback();
    const shouldLoadVideo = shouldLoad && !usesFrameFallback;

    const mobileSource =
      (this.isIOS || this.forceIOSMode) && this.video.dataset.iosSrc
        ? this.video.dataset.iosSrc
        : this.video.dataset.mobileSrc;
    const nextSource = this.mobileScrubQuery.matches ? mobileSource : this.video.dataset.desktopSrc;
    const nextPoster = this.mobileScrubQuery.matches
      ? this.video.dataset.mobilePoster
      : this.video.dataset.desktopPoster;

    if (nextPoster && this.video.getAttribute('poster') !== nextPoster) {
      this.video.setAttribute('poster', nextPoster);
    }

    if (!nextSource) return;

    // Keep the long-form story completely out of the initial page request.
    // Its observer attaches the selected source shortly before the section is
    // reached. Browser-shell iOS uses sprites and never needs the MP4.
    if (usesFrameFallback || (!shouldLoad && !this.video.getAttribute('src'))) {
      this.video.pause();
      this.video.preload = 'none';
      this.video.setAttribute('preload', 'none');
      this.video.dataset.pendingSrc = nextSource;
      return;
    }

    if (!force && this.video.getAttribute('src') === nextSource) {
      if (shouldLoadVideo) this._requestVideoLoad();
      return;
    }

    this.video.pause();
    this.video.setAttribute('src', nextSource);
    delete this.video.dataset.pendingSrc;
    const preloadMode = usesFrameFallback ? 'none' : shouldLoadVideo ? 'auto' : 'metadata';
    this.video.preload = preloadMode;
    this.video.setAttribute('preload', preloadMode);
    this.videoLoadRequested = false;
    this.videoDuration = 0;
    this.targetTime = 0;
    this.lastSeekAt = 0;
    this.seekStartedAt = 0;
    this.lastSeekRecoveryAt = 0;
    this.seekOnlyPendingTime = null;
    this.lastSeekOnlyFrame = -1;
    this.videoPaintPrimed = false;
    this.videoPaintPrimeAttempted = false;
    this.videoPaintPrimeRetryAt = 0;

    if (shouldLoadVideo) this._requestVideoLoad();
  }

  _requestVideoLoad() {
    if (!(this.video instanceof HTMLMediaElement)) return;

    if (this._usesFrameFallback()) {
      this.video.pause();
      this.video.preload = 'none';
      this.video.setAttribute('preload', 'none');
      return;
    }

    if (!this.video.getAttribute('src')) {
      this._selectVideoSource(false, true);
      return;
    }

    if (this.videoLoadRequested || this.reduceMotionQuery.matches) return;

    this.videoLoadRequested = true;
    this.video.preload = 'auto';
    this.video.setAttribute('preload', 'auto');

    if (this.video.readyState < 1) {
      this.video.load();
    }
  }

  _updateVideoPlayback() {
    if (!(this.video instanceof HTMLMediaElement)) return;

    if (!this.isStoryVisible || document.hidden || this.reduceMotionQuery.matches) {
      this.video.pause();
      this.video.playbackRate = 1;
      this._cancelScrubLoop();
      return;
    }

    this._requestVideoLoad();

    this._primeIOSBrowserShellPaint();
    this._ensureScrubLoop();
  }

  _primeIOSBrowserShellPaint() {
    if (
      !this.isIOSBrowserShell ||
      this.videoPaintPrimed ||
      this.videoPaintPrimeAttempted ||
      !this.isStoryVisible ||
      !this.mobileScrubQuery.matches ||
      !(this.video instanceof HTMLMediaElement) ||
      this.video.readyState < 2 ||
      document.hidden ||
      this.reduceMotionQuery.matches
    ) {
      return;
    }

    const now = performance.now();
    if (now < this.videoPaintPrimeRetryAt) return;

    const restoreTime = Number.isFinite(this.targetTime)
      ? this.targetTime
      : Number.isFinite(this.video.currentTime)
        ? this.video.currentTime
        : 0;

    this.videoPaintPrimeAttempted = true;
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.volume = 0;
    this.video.setAttribute('muted', '');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');
    this.video.playbackRate = 1;

    const settle = () => {
      window.setTimeout(() => {
        if (!(this.video instanceof HTMLMediaElement)) return;

        this.video.pause();
        this.video.playbackRate = 1;
        this.videoPaintPrimed = true;

        if (this.videoDuration > 0) {
          const target = Math.min(Math.max(restoreTime, 0), this.videoDuration);

          try {
            this.video.currentTime = target;
          } catch (error) {
            this.seekOnlyPendingTime = target;
          }
        }

        this._ensureScrubLoop();
      }, 80);
    };

    const playPromise = this.video.play();

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(settle).catch(() => {
        this.videoPaintPrimeAttempted = false;
        this.videoPaintPrimeRetryAt = performance.now() + 1200;
      });
    } else {
      settle();
    }
  }

  _scheduleSync(force) {
    this.pendingForce = this.pendingForce || force;

    if (this.frame !== null) return;

    this.frame = requestAnimationFrame(() => {
      const shouldForce = this.pendingForce;
      this.pendingForce = false;
      this.frame = null;
      this._sync(shouldForce);
    });
  }

  _sync(force = false) {
    if (!(this.video instanceof HTMLMediaElement) || !(this.stepsTrack instanceof HTMLElement) || this.steps.length === 0) return;

    // Refresh the layout cache lazily — only when something potentially
    // changed it (resize, breakpoint, metadata load, or first paint).
    if (this._cache.dirty || force) {
      this._refreshLayoutCache();
    }

    // Single batched DOM read pass: collect all rects we need this frame
    // BEFORE any class/state mutations. This prevents layout thrashing.
    const isMobile = this._cache.isMobile;
    const viewportHeight = this._getViewportHeight();
    const scrollY = window.scrollY || 0;
    this.scrollDirection = scrollY - this.lastSyncScrollY;
    this.lastSyncScrollY = scrollY;
    const anchorY = isMobile
      ? this._cache.stickyTop + this._cache.stickyHeight + Math.min(Math.max(viewportHeight * 0.035, 16), 34)
      : this._cache.anchorY;

    const stepRects = new Array(this.steps.length);
    for (let i = 0; i < this.steps.length; i++) {
      stepRects[i] = this.steps[i].getBoundingClientRect();
    }
    const storyRect = this.getBoundingClientRect();
    const storyBottom = storyRect.bottom;
    const usesContinuousStoryTimeline = this._usesContinuousStoryTimeline();
    const usesImmersiveMobileTimeline = !usesContinuousStoryTimeline && this._usesImmersiveMobileTimeline();
    const mirrorStickyRect =
      this.storyScrubProfile === 'mirrorcoil-continuous' && this.stickyMedia instanceof HTMLElement
        ? this.stickyMedia.getBoundingClientRect()
        : null;
    const immersiveProgress = usesContinuousStoryTimeline || usesImmersiveMobileTimeline
      ? this._getImmersiveMobileProgress(
          storyRect,
          mirrorStickyRect?.top,
          mirrorStickyRect?.height
        )
      : 0;

    if (usesContinuousStoryTimeline) {
      this.style.setProperty('--story-scroll-progress', immersiveProgress.toFixed(5));
    } else {
      this.style.removeProperty('--story-scroll-progress');
    }

    // ----- Pure calculations on cached values (no further DOM reads) -----
    const activeIndex = usesContinuousStoryTimeline
      ? this._getContinuousActiveIndex(immersiveProgress)
      : usesImmersiveMobileTimeline
        ? Math.round(immersiveProgress * Math.max(this.steps.length - 1, 0))
        : this._computeClosestStepIndex(stepRects, anchorY);
    this._applyActiveIndex(activeIndex, force);
    if (this._usesIOSPinMode()) {
      this._applyIOSPinState(scrollY);
    } else {
      this._clearIOSPinState({ removeMode: true });
      this._applyReleaseState(stepRects);
    }

    this._applyMobileCopyRelease(storyBottom, viewportHeight);
    this._applyMobileTextMasks(stepRects);
    if (usesContinuousStoryTimeline) {
      this._setContinuousStoryScrubTarget(immersiveProgress, force);
      this._applyContinuousChapterProgress(immersiveProgress);
    } else if (usesImmersiveMobileTimeline) {
      this._setScrubTarget(this._getScrubDuration() * immersiveProgress, force);
    } else {
      this._setScrubTargetFromSteps(stepRects, anchorY, force);
    }
  }

  _usesContinuousStoryTimeline() {
    return (
      this.storyScrubProfile === 'five-way-continuous' ||
      this.storyScrubProfile === 'five-way-intro-continuous' ||
      this.storyScrubProfile === 'push-pull-continuous' ||
      this.storyScrubProfile === 'mirrorcoil-continuous'
    );
  }

  _usesFrameAccurateContinuousScrub() {
    return (
      this.storyScrubProfile === 'mirrorcoil-continuous' ||
      this.storyScrubProfile === 'push-pull-continuous' ||
      this.storyScrubProfile === 'five-way-intro-continuous'
    );
  }

  _usesImmersiveMobileTimeline() {
    return this.mobileScrubQuery.matches && this.storyVariant === 'standard';
  }

  _getImmersiveMobileProgress(
    storyRect,
    stickyTop = this._cache.stickyTop,
    stickyHeight = this._cache.stickyHeight
  ) {
    if (this._usesIOSPinMode() && this._cache.pinEndY > this._cache.pinStartY) {
      const scrollY = window.scrollY || 0;
      const pinTravel = Math.max(this._cache.pinEndY - this._cache.pinStartY, 1);
      return Math.min(Math.max((scrollY - this._cache.pinStartY) / pinTravel, 0), 1);
    }

    const resolvedStickyTop = Number.isFinite(stickyTop) ? stickyTop : this._cache.stickyTop;
    const resolvedStickyHeight = Number.isFinite(stickyHeight) ? stickyHeight : this._cache.stickyHeight;
    const travel = Math.max(storyRect.height - resolvedStickyHeight, 1);
    const distance = resolvedStickyTop - storyRect.top;

    return Math.min(Math.max(distance / travel, 0), 1);
  }

  _setContinuousStoryScrubTarget(progress, force = false) {
    const scrubDuration = this._getScrubDuration();
    const lastFrame = Math.max(this.storyFrameCount - 1, 0);
    if (scrubDuration <= 0 || lastFrame <= 0) return;

    // MirrorCoil reaches its authored first frame exactly at the story boundary,
    // then clears the short static opening within the first sliver of scroll.
    // Snapping that boundary also prevents a stale forward-playback frame from
    // flashing when the user quickly enters and exits the sticky section.
    if (this.storyScrubProfile === 'mirrorcoil-continuous') {
      const clampedProgress = Math.min(Math.max(progress, 0), 1);
      const firstVisibleFrame = Math.min(6, lastFrame);
      const entryRampEnd = 0.018;

      if (clampedProgress <= 0.0001) {
        const currentTime = Number.isFinite(this.video.currentTime) ? this.video.currentTime : 0;
        const frameDuration = scrubDuration / lastFrame;
        const shouldSnapFirstFrame = force || currentTime > frameDuration * 0.5;

        this.video.pause();
        this.video.playbackRate = 1;
        this._setScrubTarget(0, shouldSnapFirstFrame);
        return;
      }

      const targetFrame = clampedProgress < entryRampEnd
        ? (clampedProgress / entryRampEnd) * firstVisibleFrame
        : firstVisibleFrame +
          ((clampedProgress - entryRampEnd) / (1 - entryRampEnd)) * (lastFrame - firstVisibleFrame);
      this._setScrubTarget((targetFrame / lastFrame) * scrubDuration, force);
      return;
    }

    if (
      this.storyScrubProfile === 'push-pull-continuous' ||
      this.storyScrubProfile === 'five-way-intro-continuous'
    ) {
      const targetFrame = Math.min(Math.max(progress, 0), 1) * lastFrame;
      this._setScrubTarget((targetFrame / lastFrame) * scrubDuration, force);
      return;
    }

    // Skip the static opening/closing holds while preserving all five passive
    // detents, the passive-to-active transition, and all five active positions.
    const timeline = [
      [0.0, 11],
      [0.44, 51],
      [0.48, 68],
      [0.52, 74],
      [1.0, Math.min(120, lastFrame)],
    ];
    const targetFrame = this._frameFromLinearTimeline(progress, timeline);
    this._setScrubTarget((targetFrame / lastFrame) * scrubDuration, force);
  }

  _frameFromLinearTimeline(progress, timeline) {
    const clampedProgress = Math.min(Math.max(progress, 0), 1);

    for (let index = 0; index < timeline.length - 1; index++) {
      const [startProgress, startFrame] = timeline[index];
      const [endProgress, endFrame] = timeline[index + 1];
      if (clampedProgress > endProgress) continue;

      const localProgress = (clampedProgress - startProgress) / Math.max(endProgress - startProgress, 0.0001);
      return startFrame + (endFrame - startFrame) * localProgress;
    }

    return timeline[timeline.length - 1][1];
  }

  _applyContinuousChapterProgress(progress) {
    const clampedProgress = Math.min(Math.max(progress, 0), 1);
    const boundaries = this._getContinuousChapterBoundaries();

    this.progressItems.forEach((item, index) => {
      const chapterStart = boundaries[index] ?? 0;
      const chapterEnd = boundaries[index + 1] ?? 1;
      const chapterProgress = Math.min(
        Math.max((clampedProgress - chapterStart) / Math.max(chapterEnd - chapterStart, 0.0001), 0),
        1
      );
      item.style.setProperty('--story-chapter-progress', chapterProgress.toFixed(5));
    });
  }

  _getContinuousActiveIndex(progress) {
    const clampedProgress = Math.min(Math.max(progress, 0), 1);
    const boundaries = this._getContinuousChapterBoundaries();
    let activeIndex = 0;

    for (let index = 1; index < boundaries.length - 1; index++) {
      if (clampedProgress >= boundaries[index]) activeIndex = index;
      else break;
    }

    return Math.min(activeIndex, Math.max(this.steps.length - 1, 0));
  }

  _getContinuousChapterBoundaries() {
    const chapterCount = Math.max(this.progressItems.length, this.steps.length, 1);
    const lastFrame = Math.max(this.storyFrameCount - 1, 1);
    const hasConfiguredFrames =
      this.storyChapterFrames.length === chapterCount + 1 &&
      this.storyChapterFrames[0] === 0 &&
      this.storyChapterFrames[this.storyChapterFrames.length - 1] === lastFrame;

    if (hasConfiguredFrames) {
      return this.storyChapterFrames.map((frame) => Math.min(Math.max(frame / lastFrame, 0), 1));
    }

    return Array.from({ length: chapterCount + 1 }, (_, index) => index / chapterCount);
  }

  _refreshLayoutCache() {
    const nextIsMobile = this.mediaQuery.matches;
    this._cache.isMobile = nextIsMobile;

    const mediaAnchor =
      this._cache.isMobile && this.mediaColumn instanceof HTMLElement ? this.mediaColumn : this.stickyMedia;

    if (mediaAnchor instanceof HTMLElement) {
      // Mobile CSS defines the sticky offset through --story-top; reading the
      // custom property keeps Safari toolbar resizes from perturbing the cache.
      // Desktop still reads the sticky element's computed top.
      const computedTop = this._cache.isMobile
        ? parseFloat(window.getComputedStyle(this).getPropertyValue('--story-top'))
        : parseFloat(window.getComputedStyle(mediaAnchor).top);
      this._cache.stickyTop = Number.isFinite(computedTop) ? computedTop : 0;
      const rect = mediaAnchor.getBoundingClientRect();
      this._cache.stickyHeight = rect.height || window.innerHeight;

      if (this._usesIOSPinMode() && this.mediaColumn instanceof HTMLElement) {
        const scrollY = window.scrollY || 0;
        const storyRect = this.getBoundingClientRect();
        const storyDocTop = scrollY + storyRect.top;
        const storyDocBottom = storyDocTop + storyRect.height;

        this._cache.pinStartY = storyDocTop - this._cache.stickyTop;
        this._cache.pinEndY = Math.max(
          this._cache.pinStartY,
          storyDocBottom - this._cache.stickyTop - this._cache.stickyHeight
        );
      } else {
        this._cache.pinStartY = 0;
        this._cache.pinEndY = 0;
      }
    } else {
      this._cache.stickyTop = 0;
      this._cache.stickyHeight = window.innerHeight;
      this._cache.pinStartY = 0;
      this._cache.pinEndY = 0;
    }

    this._cache.anchorY = this._cache.stickyTop + this._cache.stickyHeight * 0.5;

    this.iosPortalGeometryDirty = true;
    this._cache.dirty = false;
  }

  // iOS phones avoid native sticky/fixed story pins because Safari can wobble
  // video layers against the browser chrome. While active, move the same media
  // node into a tiny portal attached to the real sticky header so both share
  // one vertical anchor.
  _applyIOSPinState(scrollY) {
    if (!(this.mediaColumn instanceof HTMLElement)) return;

    if (!this._usesIOSPinMode()) {
      this._clearIOSPinState({ removeMode: true });
      return;
    }

    const isReleased = this.classList.contains('is-story-released');
    const shouldRelease = isReleased
      ? scrollY >= this._cache.pinEndY - this.releaseHysteresisPx
      : scrollY >= this._cache.pinEndY;
    const pinStartY = this.mediaIsPortaled
      ? this._cache.pinStartY - this.releaseHysteresisPx
      : this._cache.pinStartY;
    const shouldPin = scrollY >= pinStartY && !shouldRelease;

    if (shouldRelease) {
      if (this.mediaIsPortaled) {
        // Once the pin is complete, return the media to the story and anchor it
        // there. Keeping a viewport-sized video in the fixed portal and moving
        // it with a per-scroll transform makes Safari repaint that layer after
        // every story handoff, which presents as a low-FPS vertical wobble.
        this._releasePortaledMedia();
      } else if (!this.classList.contains('is-story-released')) {
        this._moveMediaToReleaseSlot();
      }

      this.classList.remove('is-ios-pinned');
      return;
    }

    if (shouldPin) {
      if (this.classList.contains('is-story-released')) {
        this._clearReleaseState();
      }

      this.classList.add('is-ios-pinned');
      this._activateMediaSpacer();
      this._moveMediaToIOSPortal();
      return;
    }

    if (this.classList.contains('is-story-released')) {
      this._clearReleaseState();
    }

    this._clearIOSPinState();
  }

  _clearIOSPinState({ removeMode = false } = {}) {
    this.classList.remove('is-ios-pinned', 'is-ios-transform-pin');
    this._restoreMediaFromIOSPortal();

    if (removeMode) {
      this.classList.remove('is-ios-fixed-pin');
    }

    if (this.mediaColumn instanceof HTMLElement) {
      this.mediaColumn.style.removeProperty('--story-pin-offset');
      this.mediaColumn.style.removeProperty('--story-mobile-media-height');
      this.mediaColumn.style.removeProperty('--story-surface');
    }

    this._pinOffset = 0;
    this._pinOffsetApplied = false;

    if (!this.classList.contains('is-story-released')) {
      this._deactivateMediaSpacer();
    }
  }

  _applyMobileTextMasks(stepRects) {
    if (!Array.isArray(stepRects) || stepRects.length === 0) return;

    if (this.mobileScrubQuery.matches && this.mobilePanels.length > 0 && this.storyVariant !== 'five-way') {
      this.steps.forEach((step) => {
        if (!(step instanceof HTMLElement)) return;
        step.classList.remove('is-text-masked');
        step.style.removeProperty('--story-step-text-mask-top');
      });
      return;
    }

    const shouldMask =
      this._cache.isMobile && !this.classList.contains('is-story-released') && this._cache.stickyHeight > 0;
    const maskBoundary = this._cache.stickyTop + this._cache.stickyHeight;

    this.steps.forEach((step, index) => {
      if (!(step instanceof HTMLElement)) return;

      const rect = stepRects[index];
      const maskTop = shouldMask && rect ? Math.max(maskBoundary - rect.top, 0) : 0;

      if (maskTop <= 0.5) {
        step.classList.remove('is-text-masked');
        step.style.removeProperty('--story-step-text-mask-top');
        return;
      }

      step.classList.add('is-text-masked');
      step.style.setProperty('--story-step-text-mask-top', `${Math.round(maskTop * 1000) / 1000}px`);
    });
  }

  _ensureIOSPortal() {
    if (this.iosPortal instanceof HTMLElement && this.iosPortal.isConnected) {
      return this.iosPortal;
    }

    this.iosPortal = document.createElement('div');
    this.iosPortal.className = 'neuracoil-sticky-story__ios-portal';
    this.iosPortal.dataset.storyScrubProfile = this.storyScrubProfile;
    if (this.closest('.neuracoil-mirror-story')) {
      this.iosPortal.classList.add('neuracoil-mirror-story');
    }
    this.iosPortal.setAttribute('data-story-ios-portal', '');
    this._updateBrowserShellClass();
    document.body.append(this.iosPortal);

    return this.iosPortal;
  }

  _getHeaderBottom() {
    const headerRect = this.header instanceof HTMLElement ? this.header.getBoundingClientRect() : null;
    if (Number.isFinite(headerRect?.bottom)) return Math.max(headerRect.bottom, 0);

    return Math.max(this._cache.stickyTop || 0, 0);
  }

  _syncMobileVisualViewport() {
    if (!(this.mediaColumn instanceof HTMLElement)) return;

    if (!this.mobileScrubQuery.matches) {
      this.mediaColumn.style.removeProperty('--story-visual-bottom-inset');
      return;
    }

    // The iOS portal already adopts the live visual-viewport height. Applying
    // the Android offset as well would move its progress UI twice.
    if (this._usesIOSPinMode()) {
      this.mediaColumn.style.removeProperty('--story-visual-bottom-inset');
      this._syncIOSPortalVisualViewport();
      return;
    }

    const visualViewport = window.visualViewport;
    const visualBottom =
      (visualViewport?.offsetTop || 0) +
      (visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0);
    const stableBottom = Math.max(this._cache.stickyTop + this._cache.stickyHeight, 0);
    const bottomInset = Math.round((stableBottom - visualBottom) * 1000) / 1000;
    const bottomInsetValue = `${bottomInset}px`;

    if (this.mediaColumn.style.getPropertyValue('--story-visual-bottom-inset') !== bottomInsetValue) {
      this.mediaColumn.style.setProperty('--story-visual-bottom-inset', bottomInsetValue);
    }
  }

  _syncIOSPortalVisualViewport() {
    const portalTop = Math.max(this.iosPortalTop || this._cache.stickyTop || 0, 0);
    const portalTopValue = `${Math.round(portalTop * 1000) / 1000}px`;
    const visualViewportHeight = window.visualViewport?.height || window.innerHeight || 0;
    const dynamicHeight = Math.max(visualViewportHeight - portalTop, 0);
    const portalHeight = dynamicHeight > 0 ? dynamicHeight : Math.max(this.iosPortalHeight, 0);
    const portalHeightValue = `${Math.round(portalHeight)}px`;

    if (this.iosPortal instanceof HTMLElement) {
      if (this.iosPortal.style.getPropertyValue('--story-ios-portal-top') !== portalTopValue) {
        this.iosPortal.style.setProperty('--story-ios-portal-top', portalTopValue);
      }

      if (this.iosPortal.style.getPropertyValue('--story-ios-portal-height') !== portalHeightValue) {
        this.iosPortal.style.setProperty('--story-ios-portal-height', portalHeightValue);
      }
    }

    if (
      this.mediaColumn instanceof HTMLElement &&
      this.mediaColumn.style.getPropertyValue('--story-mobile-media-height') !== portalHeightValue
    ) {
      this.mediaColumn.style.setProperty('--story-mobile-media-height', portalHeightValue);
    }
  }

  _refreshIOSPortalGeometry(portal = this.iosPortal) {
    if (!(portal instanceof HTMLElement) || !(this.mediaColumn instanceof HTMLElement)) return;

    const measuredHeight = this._cache.stickyHeight || this.mediaColumn.getBoundingClientRect().height || 0;
    const mediaHeight = Math.max(measuredHeight, 0);
    const portalTop = this._getHeaderBottom();
    const computedStoryStyles = window.getComputedStyle(this);
    const copiedProperties = [
      '--neuracoil-shell-gutter',
      '--story-mobile-media-height',
      '--story-mobile-media-size',
      '--story-mobile-media-top',
      '--story-mobile-text-fade-height',
      '--story-stage-height',
      '--story-surface',
      '--story-top',
      '--story-video-ambient-height',
    ];

    this.iosPortalTop = portalTop;
    this.iosPortalHeight = mediaHeight;
    portal.style.setProperty('--story-ios-portal-height', `${mediaHeight}px`);
    this.mediaColumn.style.setProperty('--story-mobile-media-height', `${mediaHeight}px`);

    copiedProperties.forEach((property) => {
      const value = computedStoryStyles.getPropertyValue(property).trim();
      if (!value) return;

      portal.style.setProperty(property, value);
      this.mediaColumn.style.setProperty(property, value);
    });

    this.iosPortalGeometryDirty = false;
    this._syncIOSPortalVisualViewport();
  }

  _moveMediaToIOSPortal() {
    if (!(this.mediaColumn instanceof HTMLElement)) return;

    if (
      this.mediaIsPortaled &&
      this.iosPortal instanceof HTMLElement &&
      this.iosPortal.isConnected &&
      this.mediaColumn.parentNode === this.iosPortal
    ) {
      if (this.iosPortalGeometryDirty) this._refreshIOSPortalGeometry();
      return;
    }

    const portal = this._ensureIOSPortal();
    if (!(portal instanceof HTMLElement)) return;

    this._refreshIOSPortalGeometry(portal);

    if (this.mediaColumn.parentNode !== portal) {
      portal.append(this.mediaColumn);
    }

    this.mediaIsPortaled = true;
    this.classList.add('is-ios-portaled');
    this._syncIOSPortalVisualViewport();
  }

  _restoreMediaFromIOSPortal() {
    if (!this.mediaIsPortaled || !(this.mediaColumn instanceof HTMLElement)) {
      this._removeEmptyIOSPortal();
      return;
    }

    const parent = this.originalMediaParent;
    if (parent instanceof Node) {
      if (this.originalMediaNextSibling instanceof Node && this.originalMediaNextSibling.parentNode === parent) {
        parent.insertBefore(this.mediaColumn, this.originalMediaNextSibling);
      } else {
        parent.append(this.mediaColumn);
      }
    }

    this.mediaIsPortaled = false;
    this.iosPortalGeometryDirty = true;
    this.iosPortalTop = 0;
    this.iosPortalHeight = 0;
    this.classList.remove('is-ios-portaled');
    this.mediaColumn.style.removeProperty('--story-visual-bottom-inset');
    this.iosPortal?.style.removeProperty('--story-ios-release-offset');
    [
      '--neuracoil-shell-gutter',
      '--story-mobile-media-height',
      '--story-mobile-media-size',
      '--story-mobile-media-top',
      '--story-mobile-text-fade-height',
      '--story-stage-height',
      '--story-surface',
      '--story-top',
      '--story-video-ambient-height',
    ].forEach((property) => this.mediaColumn.style.removeProperty(property));

    this._removeEmptyIOSPortal();
  }

  _removeEmptyIOSPortal() {
    if (!(this.iosPortal instanceof HTMLElement)) return;
    if (this.mediaColumn instanceof HTMLElement && this.iosPortal.contains(this.mediaColumn)) return;

    this.iosPortal.remove();
    this.iosPortal = null;
  }

  _releasePortaledMedia() {
    if (!(this.mediaColumn instanceof HTMLElement)) return;

    const beforeRect = this.mediaColumn.getBoundingClientRect();
    const layoutRect = this.layout instanceof HTMLElement ? this.layout.getBoundingClientRect() : null;
    const releaseTop = layoutRect ? beforeRect.top - layoutRect.top : beforeRect.top + window.scrollY;

    this._activateMediaSpacer(beforeRect.height);
    this._restoreMediaFromIOSPortal();

    this.style.setProperty('--story-release-top', `${Math.max(releaseTop, 0)}px`);
    this.style.setProperty('--story-release-height', `${Math.max(beforeRect.height, 0)}px`);
    this.classList.add('is-story-released');
  }

  _applyActiveIndex(index, force = false) {
    if (index === this.activeIndex && !force) return;

    this.activeIndex = index;

    this.steps.forEach((step, stepIndex) => {
      step.classList.toggle('is-active', stepIndex === index);
      step.dataset.state = stepIndex < index ? 'past' : stepIndex === index ? 'active' : 'future';
    });

    this.mobilePanels.forEach((panel, panelIndex) => {
      panel.classList.toggle('is-active', panelIndex === index);
      panel.dataset.state = panelIndex < index ? 'past' : panelIndex === index ? 'active' : 'future';
    });

    this.progressItems.forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === index);
      item.classList.toggle('is-past', itemIndex < index);
    });

    if (this.currentChapter instanceof HTMLElement) {
      this.currentChapter.textContent = String(index + 1).padStart(2, '0');
    }
  }

  _applyMobileCopyRelease(storyBottom, viewportHeight) {
    this.classList.remove('is-mobile-copy-releasing');
    this.mobileCopyStage?.classList.remove('is-releasing');

    if (
      !this.mobileScrubQuery.matches ||
      !this._usesContinuousStoryTimeline() ||
      !(this.mediaColumn instanceof HTMLElement)
    ) {
      this.mediaColumn?.style.removeProperty('--story-handoff-opacity');
      return;
    }

    // Fade the complete stage—video, copy, and progress—as the following
    // section enters. Fading only the copy leaves a conspicuous video-only tail.
    // Using the live visual viewport keeps the handoff aligned while mobile
    // browser chrome expands or retracts without changing the svh layout.
    const visualViewport = window.visualViewport;
    const visualViewportHeight = visualViewport?.height || viewportHeight;
    const visualBottom = (visualViewport?.offsetTop || 0) + visualViewportHeight;
    // Give the following section enough travel to replace the story visually.
    // This remains tied to the real section boundary on iOS as well: fading at
    // the portal release point would leave an almost full blank viewport while
    // the following section has only just entered at the bottom.
    const fadeDistance = Math.min(Math.max(visualViewportHeight * 0.42, 260), 420);
    const rawProgress = Math.min(Math.max((visualBottom - storyBottom) / fadeDistance, 0), 1);
    const easedProgress = rawProgress * rawProgress * (3 - 2 * rawProgress);
    const opacityValue = (1 - easedProgress).toFixed(4);

    if (this.mediaColumn.style.getPropertyValue('--story-handoff-opacity') !== opacityValue) {
      this.mediaColumn.style.setProperty('--story-handoff-opacity', opacityValue);
    }
  }

  _applyReleaseState(stepRects) {
    // The phone breakpoint releases naturally: iOS through the transform clamp,
    // other phones through native sticky. This absolute release path only covers
    // the 749–989px tablet range.
    if (
      !this._cache.isMobile ||
      this.mobileScrubQuery.matches ||
      !(this.mediaColumn instanceof HTMLElement) ||
      stepRects.length === 0
    ) {
      this._clearReleaseState();
      return;
    }

    const releaseRect = stepRects[stepRects.length - 1];
    const isReleased = this.classList.contains('is-story-released');
    const releaseY = this._cache.stickyTop + this._cache.stickyHeight + Math.min(Math.max(this._getViewportHeight() * 0.045, 18), 44);
    const shouldRelease = releaseRect.top <= releaseY + (isReleased ? this.releaseHysteresisPx : 0);

    if (!shouldRelease) {
      this._clearReleaseState();
      return;
    }

    if (isReleased) return;
    if (this.scrollDirection < 0) return;

    this._moveMediaToReleaseSlot();
  }

  _moveMediaToReleaseSlot() {
    if (!(this.mediaColumn instanceof HTMLElement)) return;

    const beforeRect = this.mediaColumn.getBoundingClientRect();
    const layoutRect = this.layout instanceof HTMLElement ? this.layout.getBoundingClientRect() : null;
    const releaseTop = layoutRect ? beforeRect.top - layoutRect.top : beforeRect.top + window.scrollY;

    this._activateMediaSpacer(beforeRect.height);

    this.style.setProperty('--story-release-top', `${Math.max(releaseTop, 0)}px`);
    this.style.setProperty('--story-release-height', `${Math.max(beforeRect.height, 0)}px`);
    this.classList.add('is-story-released');
  }

  _clearReleaseState() {
    if (!this.classList.contains('is-story-released')) return;

    this.classList.remove('is-story-released');
    this.style.removeProperty('--story-release-top');
    this.style.removeProperty('--story-release-height');

    this.iosPortal?.style.removeProperty('--story-ios-release-offset');

    if (this.mediaIsPortaled) {
      this._activateMediaSpacer();
    } else {
      this._deactivateMediaSpacer();
    }
  }

  _activateMediaSpacer(height = null) {
    if (!(this.mediaSpacer instanceof HTMLElement)) return;

    const spacerHeight =
      Number.isFinite(height) && height !== null
        ? height
        : this._cache.stickyHeight || this.mediaColumn?.getBoundingClientRect().height || 0;

    this.mediaSpacer.style.height = `${Math.max(spacerHeight, 0)}px`;
    this.mediaSpacer.classList.add('is-active');
  }

  _deactivateMediaSpacer() {
    if (!(this.mediaSpacer instanceof HTMLElement)) return;

    this.mediaSpacer.classList.remove('is-active');
    this.mediaSpacer.style.removeProperty('height');
  }

  _setScrubTargetFromSteps(stepRects, anchorY, force = false) {
    const scrubDuration = this._getScrubDuration();
    if (scrubDuration <= 0 || stepRects.length === 0) return;

    if (this.storyVariant === 'five-way' && stepRects.length > 1) {
      this._setSegmentedScrubTarget(stepRects, anchorY, scrubDuration, force);
      return;
    }

    const anchors = stepRects.map((rect) => {
      const baseStepAnchorOffset = this._cache.isMobile
        ? Math.min(Math.max(rect.height * 0.14, 44), 112)
        : rect.height * 0.5;
      const stepAnchorOffset = Math.max(baseStepAnchorOffset - rect.height * this.storyStartOffsetRatio, 0);

      return window.scrollY + rect.top + stepAnchorOffset - anchorY;
    });
    const chapterCount = anchors.length;

    if (chapterCount === 1) {
      this._setScrubTarget(this._chapterTimeForIndex(0, chapterCount, scrubDuration), force);
      return;
    }

    const gaps = anchors.slice(1).map((anchor, index) => anchor - anchors[index]).filter((gap) => gap > 0);
    const smallestGap = gaps.length > 0 ? Math.min(...gaps) : 0;
    const holdRatio = this.chapterHoldRatio;
    const holdMin = this._cache.isMobile ? 44 : 56;
    const holdMax = this._cache.isMobile ? 92 : 120;
    const holdPx = Math.min(Math.max(smallestGap * holdRatio, holdMin), holdMax);
    const scrollY = window.scrollY;

    if (scrollY <= anchors[0] + holdPx) {
      this._setScrubTarget(this._chapterTimeForIndex(0, chapterCount, scrubDuration), force);
      return;
    }

    for (let index = 0; index < chapterCount - 1; index++) {
      const currentAnchor = anchors[index];
      const nextAnchor = anchors[index + 1];
      const currentTime = this._chapterTimeForIndex(index, chapterCount, scrubDuration);
      const nextTime = this._chapterTimeForIndex(index + 1, chapterCount, scrubDuration);
      const leaveCurrent = currentAnchor + holdPx;
      const enterNext = nextAnchor - holdPx;

      if (scrollY <= leaveCurrent) {
        this._setScrubTarget(currentTime, force);
        return;
      }

      if (scrollY < enterNext) {
        const localProgress = (scrollY - leaveCurrent) / Math.max(enterNext - leaveCurrent, 1);
        const easedProgress = this._smoothstep(localProgress);
        this._setScrubTarget(currentTime + (nextTime - currentTime) * easedProgress, force);
        return;
      }

      if (scrollY <= nextAnchor + holdPx) {
        this._setScrubTarget(nextTime, force);
        return;
      }
    }

    this._setScrubTarget(this._chapterTimeForIndex(chapterCount - 1, chapterCount, scrubDuration), force);
  }

  _setSegmentedScrubTarget(stepRects, anchorY, scrubDuration, force = false) {
    const scrollY = window.scrollY || 0;
    const viewportHeight = this._getViewportHeight();
    const segmentAnchorY = anchorY;
    const firstChapterLead = viewportHeight * this.storyStartOffsetRatio;
    const lastFrame = Math.max(this.storyFrameCount - 1, 0);
    const chapterFrameStep = this.storyFrameStep > 0 ? this.storyFrameStep : lastFrame / stepRects.length;
    const documentTops = stepRects.map((rect) => scrollY + rect.top);
    const passiveTimeline = [
      [0.0, 5],
      [0.025, 5],
      [0.07, 19],
      [0.085, 19],
      [0.13, 29],
      [0.145, 29],
      [0.19, 41],
      [0.205, 41],
      [0.25, 53],
      [0.265, 53],
      [0.34, 74],
      [1.0, 74],
    ];
    const activeTimeline = [
      [0.0, 74],
      [0.03, 74],
      [0.085, 88],
      [0.11, 88],
      [0.165, 100],
      [0.19, 100],
      [0.245, 112],
      [0.27, 112],
      [0.35, lastFrame],
      [1.0, lastFrame],
    ];

    for (let index = 0; index < stepRects.length; index++) {
      const timeline = index === 0 ? passiveTimeline : index === 1 ? activeTimeline : null;
      const chapterStartFrame = timeline
        ? timeline[0][1]
        : Math.min(index * chapterFrameStep, lastFrame);
      const chapterEndFrame = timeline
        ? timeline[timeline.length - 1][1]
        : Math.min((index + 1) * chapterFrameStep, lastFrame);
      const chapterStartTime = lastFrame > 0 ? (chapterStartFrame / lastFrame) * scrubDuration : 0;
      const segmentStart =
        documentTops[index] - segmentAnchorY - (index === 0 ? firstChapterLead : 0);
      const segmentEnd =
        index < stepRects.length - 1
          ? documentTops[index + 1] - segmentAnchorY
          : documentTops[index] + stepRects[index].height - segmentAnchorY;

      if (scrollY <= segmentStart) {
        this._setScrubTarget(chapterStartTime, force);
        return;
      }

      if (scrollY < segmentEnd) {
        const localProgress = (scrollY - segmentStart) / Math.max(segmentEnd - segmentStart, 1);
        const targetFrame = timeline
          ? this._frameFromTimeline(localProgress, timeline)
          : chapterStartFrame + (chapterEndFrame - chapterStartFrame) * this._smoothstep(localProgress);
        this._setScrubTarget(lastFrame > 0 ? (targetFrame / lastFrame) * scrubDuration : 0, force);
        return;
      }
    }

    this._setScrubTarget(scrubDuration, force);
  }

  _frameFromTimeline(progress, timeline) {
    const clampedProgress = Math.min(Math.max(progress, 0), 1);

    for (let index = 0; index < timeline.length - 1; index++) {
      const [startProgress, startFrame] = timeline[index];
      const [endProgress, endFrame] = timeline[index + 1];

      if (clampedProgress > endProgress) continue;

      if (startFrame === endFrame) return startFrame;

      const localProgress = (clampedProgress - startProgress) / Math.max(endProgress - startProgress, 0.0001);
      return startFrame + (endFrame - startFrame) * this._smoothstep(localProgress);
    }

    return timeline[timeline.length - 1][1];
  }

  _chapterTimeForIndex(index, chapterCount, scrubDuration = this._getScrubDuration()) {
    if (scrubDuration <= 0) return 0;

    const lastFrame = Math.max(this.storyFrameCount - 1, 0);
    if (lastFrame > 0 && this.storyFrameStep > 0) {
      const frame = index === chapterCount - 1 ? lastFrame : Math.min(index * this.storyFrameStep, lastFrame);
      return (frame / lastFrame) * scrubDuration;
    }

    return chapterCount > 1 ? (index / (chapterCount - 1)) * scrubDuration : 0;
  }

  _smoothstep(value) {
    const progress = Math.min(Math.max(value, 0), 1);
    return progress * progress * (3 - 2 * progress);
  }

  _setScrubTarget(time, force = false) {
    const scrubDuration = this._getScrubDuration();
    if (!(this.video instanceof HTMLMediaElement) || scrubDuration <= 0) return;

    const nextTargetTime = Math.min(Math.max(time, 0), scrubDuration);
    const storyProgress = nextTargetTime / scrubDuration;
    this.targetDirection = nextTargetTime - this.targetTime;
    this.targetTime = nextTargetTime;

    if (this.storyScrubProfile === 'mirrorcoil-continuous' && this.targetDirection < -0.002) {
      this.video.pause();
      this.video.playbackRate = 1;
    }

    this.style.setProperty('--story-progress', storyProgress.toFixed(5));
    if (this.storyScrubProfile === 'push-pull-continuous' && this.mediaColumn instanceof HTMLElement) {
      const finalChapterProgress = this._smoothstep((storyProgress - 0.84) / 0.16);
      this.mediaColumn.style.setProperty('--story-final-media-shift', `${(finalChapterProgress * 5.5).toFixed(3)}%`);
    }
    if (this.accessoryStoryRoot instanceof HTMLElement) {
      if (this.storyVariant === 'five-way') {
        this.accessoryStoryRoot.style.setProperty('--story-slot-shift', `${(-12 + storyProgress * 24).toFixed(1)}px`);
      } else {
        this.accessoryStoryRoot.style.setProperty('--story-knurl-shift', `${(-96 + storyProgress * 192).toFixed(1)}px`);
      }
    }

    if (this._usesFrameFallback()) {
      this._setFrameFallbackForTime(nextTargetTime, scrubDuration);
    }

    if (force && this.videoDuration > 0 && Number.isFinite(this.video.currentTime)) {
      if (this._usesSeekOnlyScrub()) {
        this._seekVideoForCurrentTarget(true);
      } else {
        this._seekVideo(this.targetTime, true, true);
      }
    }

    this._ensureScrubLoop();
  }

  _ensureScrubLoop() {
    if (this.scrubFrame !== null) return;

    this.scrubFrame = requestAnimationFrame(this._tickScrub);
  }

  _cancelScrubLoop() {
    if (this.scrubFrame === null) return;

    cancelAnimationFrame(this.scrubFrame);
    this.scrubFrame = null;
  }

  _usesPausedMobileScrub() {
    return this._usesSeekOnlyScrub();
  }

  _usesSeekOnlyScrub() {
    return (this.isIOS || this.forceIOSMode) && this.mobileScrubQuery.matches;
  }

  _usesFrameFallback() {
    return (
      !this.frameFallbackFailed &&
      this._shouldUseIOSFrameFallback() &&
      this.mobileScrubQuery.matches &&
      this.frameFallback instanceof HTMLElement &&
      this.storyFrameSprites.length > 0
    );
  }

  _shouldUseIOSFrameFallback() {
    return this.isIOSBrowserShell || (this.preferIOSFrameFallback && (this.isIOS || this.forceIOSMode));
  }

  _getFrameFallbackSprites() {
    if (!(this.frameFallback instanceof HTMLElement)) return [];

    return (this.frameFallback.dataset.storyFrameSprites || '')
      .split('|')
      .map((source) => source.trim())
      .filter(Boolean);
  }

  _preloadFrameFallbackSprites() {
    if (!this._usesFrameFallback() || this.frameFallbackReady || this.frameFallbackFailed) return;
    if (this.frameFallbackLoadPromise) return;

    const spriteLoads = this.storyFrameSprites.map((source) => {
      const image = new Image();
      image.decoding = 'async';
      this.frameFallbackPreloads.push(image);

      return new Promise((resolve, reject) => {
        const fail = () => reject(new Error(`Unable to load story sprite: ${source}`));
        const finish = async () => {
          try {
            if (typeof image.decode === 'function') await image.decode();
            if (image.naturalWidth <= 0 || image.naturalHeight <= 0) throw new Error('Story sprite is empty');
            resolve(image);
          } catch (error) {
            reject(error);
          }
        };

        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', fail, { once: true });
        image.src = source;
      });
    });

    this.frameFallbackLoadPromise = Promise.all(spriteLoads)
      .then(() => {
        if (this.frameFallbackFailed) return;

        this.frameFallbackReady = true;
        this._setFrameFallbackForTime(this.targetTime, this._getFallbackTimelineDuration());
        this._syncFrameFallbackReadyClass();
      })
      .catch(() => {
        this._disableFrameFallback();
      });
  }

  _disableFrameFallback() {
    if (this.frameFallbackFailed) return;

    this.frameFallbackFailed = true;
    this.frameFallbackReady = false;
    this._syncFrameFallbackReadyClass();

    if (this.frameFallback instanceof HTMLElement) {
      this.frameFallback.style.removeProperty('background-image');
      this.frameFallback.style.removeProperty('--story-frame-x');
      this.frameFallback.style.removeProperty('--story-frame-y');
    }

    this.lastFrameFallbackFrame = -1;
    this.lastFrameFallbackSprite = -1;
    this._selectVideoSource(true, true);
    this._scheduleSync(true);

    if (this.isStoryVisible) this._updateVideoPlayback();
  }

  _prepareFrameFallback() {
    if (!this._usesFrameFallback()) return;

    this._preloadFrameFallbackSprites();
  }

  _getSeekOnlyFrameDuration(scrubDuration = this._getScrubDuration()) {
    const frameCount = Math.max(this.storyFrameCount - 1, 1);
    const durationFrame = scrubDuration > 0 ? scrubDuration / frameCount : 0;

    return durationFrame > 0 ? durationFrame : 1 / 30;
  }

  _setFrameFallback(frame, force = false) {
    if (!(this.frameFallback instanceof HTMLElement)) return;

    const lastFrame = Math.max(this.storyFrameCount - 1, 0);
    const nextFrame = Math.min(Math.max(Math.round(frame), 0), lastFrame);
    if (!force && nextFrame === this.lastFrameFallbackFrame) return;

    const cols = Math.max(this.storyFrameSpriteCols, 1);
    const rows = Math.max(this.storyFrameSpriteRows, 1);
    const sheetSize = Math.max(this.storyFrameSpriteSize, 1);
    const sheetIndex = Math.min(Math.floor(nextFrame / sheetSize), this.storyFrameSprites.length - 1);
    const sheetFrame = nextFrame - sheetIndex * sheetSize;
    const col = sheetFrame % cols;
    const row = Math.floor(sheetFrame / cols);
    const x = cols > 1 ? `${(col / (cols - 1)) * 100}%` : '0%';
    const y = rows > 1 ? `${(row / (rows - 1)) * 100}%` : '0%';

    if (sheetIndex !== this.lastFrameFallbackSprite) {
      this.frameFallback.style.backgroundImage = `url("${this.storyFrameSprites[sheetIndex]}")`;
      this.lastFrameFallbackSprite = sheetIndex;
    }

    this.frameFallback.style.setProperty('--story-frame-cols', String(cols));
    this.frameFallback.style.setProperty('--story-frame-rows', String(rows));
    this.frameFallback.style.setProperty('--story-frame-bg-width', `${cols * 100}%`);
    this.frameFallback.style.setProperty('--story-frame-bg-height', `${rows * 100}%`);
    this.frameFallback.style.setProperty('--story-frame-x', x);
    this.frameFallback.style.setProperty('--story-frame-y', y);
    this.lastFrameFallbackFrame = nextFrame;
  }

  _setFrameFallbackForTime(time, scrubDuration = this._getScrubDuration()) {
    if (scrubDuration <= 0) return;

    const frameDuration = this._getSeekOnlyFrameDuration(scrubDuration);
    const frame = frameDuration > 0 ? time / frameDuration : 0;
    this._setFrameFallback(frame);
  }

  _seekVideoForCurrentTarget(immediate = false) {
    if (!(this.video instanceof HTMLMediaElement) || this.videoDuration <= 0) return false;

    const frameDuration = this._getSeekOnlyFrameDuration();
    const lastFrame = Math.max(this.storyFrameCount - 1, 0);
    const targetFrame = Math.min(Math.max(Math.round(this.targetTime / frameDuration), 0), lastFrame);
    const seekTime = Math.min(targetFrame * frameDuration, this.videoDuration);
    const currentTime = Number.isFinite(this.video.currentTime) ? this.video.currentTime : 0;

    if (this._usesFrameFallback()) {
      this._setFrameFallback(targetFrame);
    }

    if (this.video.seeking) {
      const now = performance.now();
      if (this.seekStartedAt <= 0) this.seekStartedAt = now;
      if (!this._usesFrameFallback() && now - this.seekStartedAt > 900) {
        return this._recoverStalledSeek(seekTime);
      }
      this.seekOnlyPendingTime = seekTime;
      return false;
    }

    if (targetFrame === this.lastSeekOnlyFrame && Math.abs(currentTime - seekTime) < frameDuration * 0.55) {
      this.seekOnlyPendingTime = null;
      return true;
    }

    const now = performance.now();
    const minInterval = immediate ? 0 : 45;

    if (now - this.lastSeekAt < minInterval) {
      this.seekOnlyPendingTime = seekTime;
      this._ensureScrubLoop();
      return false;
    }

    this.video.pause();
    this.video.playbackRate = 1;
    this.seekOnlyPendingTime = null;
    this.lastSeekAt = now;
    this.seekStartedAt = now;
    this.lastSeekOnlyFrame = targetFrame;

    try {
      this.video.currentTime = seekTime;
    } catch (error) {
      this.seekOnlyPendingTime = seekTime;
      return false;
    }

    return true;
  }

  _tickScrub() {
    this.scrubFrame = null;

    if (!(this.video instanceof HTMLMediaElement) || this.videoDuration <= 0) return;

    if (!this.isStoryVisible || document.hidden || this.reduceMotionQuery.matches) {
      this.video.pause();
      this.video.playbackRate = 1;
      return;
    }

    const currentTime = Number.isFinite(this.video.currentTime) ? this.video.currentTime : 0;
    const delta = this.targetTime - currentTime;
    const distance = Math.abs(delta);
    const isMobileScrub = this.mobileScrubQuery.matches;
    const usesContinuousStoryTimeline = this._usesContinuousStoryTimeline();
    const settledDistance = usesContinuousStoryTimeline ? 0.018 : isMobileScrub ? 0.055 : 0.035;

    if (this._usesSeekOnlyScrub()) {
      this.video.pause();
      this.video.playbackRate = 1;
      this._seekVideoForCurrentTarget(false);

      if (Math.abs(this.targetTime - currentTime) >= settledDistance || this.seekOnlyPendingTime !== null) {
        this._ensureScrubLoop();
      }

      return;
    }

    // These stories are authored as dense 30 fps frame-by-frame scroll renders.
    // Keep it paused and seek to the latest requested frame instead of letting
    // forward playback chase the target. The shared catch-up mode has a 1x
    // minimum speed, so it can overshoot a stopped scroll target and then seek
    // backwards, which presents as a visible forward/backward flicker.
    if (this._usesFrameAccurateContinuousScrub()) {
      this._seekFrameAccurateStory(currentTime);
      return;
    }

    if (distance < settledDistance) {
      this.video.pause();
      this.video.playbackRate = 1;
      return;
    }

    if (this._usesPausedMobileScrub()) {
      this.video.pause();
      this.video.playbackRate = 1;
      this._seekVideo(this.targetTime, distance > 0.5, true, distance > 0.65 ? 90 : 140);
      return;
    }

    // Reverse playback is not available for HTML video, so backward scrolls
    // must be precise paused seeks. Keep forward playback out of this path;
    // otherwise the frame can drift forward while the user scrolls upward.
    if (this.targetDirection < -0.002 || delta < -0.08) {
      this.video.pause();
      this.video.playbackRate = 1;
      this._seekVideo(this.targetTime, distance > 0.35, true, isMobileScrub ? 120 : 48);
      this._ensureScrubLoop();
      return;
    }

    // For small forward deltas, let the decoder play normally and vary the
    // catch-up speed instead of seeking on every scroll frame.
    const forwardPlaybackWindow = usesContinuousStoryTimeline ? 1.4 : isMobileScrub ? 0.9 : 1.15;
    if (distance < forwardPlaybackWindow) {
      const playbackFactor = usesContinuousStoryTimeline ? 2.2 : isMobileScrub ? 1.15 : 1.4;
      const minimumPlaybackRate = usesContinuousStoryTimeline ? 1.0 : isMobileScrub ? 0.22 : 0.28;
      const maximumPlaybackRate = usesContinuousStoryTimeline ? 1.85 : isMobileScrub ? 1.12 : 1.35;
      this.video.playbackRate = Math.min(
        Math.max(distance * playbackFactor, minimumPlaybackRate),
        maximumPlaybackRate
      );
      const playPromise = this.video.paused ? this.video.play() : null;

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }

      this._ensureScrubLoop();
      return;
    }

    const seekLead = usesContinuousStoryTimeline ? 0.1 : 0.18;
    this._seekVideo(Math.max(this.targetTime - seekLead, 0), true, false, isMobileScrub ? 120 : 85);
    this._ensureScrubLoop();
  }

  _seekFrameAccurateStory(currentTime) {
    this.video.pause();
    this.video.playbackRate = 1;

    const frameDuration = this._getSeekOnlyFrameDuration();
    const lastFrame = Math.max(this.storyFrameCount - 1, 0);
    const targetFrame = Math.min(Math.max(Math.round(this.targetTime / frameDuration), 0), lastFrame);
    const seekTime = Math.min(targetFrame * frameDuration, this.videoDuration);

    if (Math.abs(currentTime - seekTime) < frameDuration * 0.45) {
      this.seekOnlyPendingTime = null;
      return;
    }

    const now = performance.now();
    const minInterval = 1000 / 30;

    if (this.video.seeking) {
      if (this.seekStartedAt <= 0) this.seekStartedAt = now;
      if (now - this.seekStartedAt > 900) {
        this._recoverStalledSeek(seekTime);
        return;
      }
      this.seekOnlyPendingTime = seekTime;
      this._ensureScrubLoop();
      return;
    }

    if (now - this.lastSeekAt < minInterval) {
      this.seekOnlyPendingTime = seekTime;
      this._ensureScrubLoop();
      return;
    }

    this.lastSeekAt = now;
    this.seekStartedAt = now;
    this.seekOnlyPendingTime = null;

    try {
      this.video.currentTime = seekTime;
    } catch (error) {
      this.seekOnlyPendingTime = seekTime;
    }

    if (this.seekOnlyPendingTime !== null) {
      this._ensureScrubLoop();
    }
  }

  _seekVideo(time, immediate = false, precise = false, minInterval = 85) {
    const now = performance.now();
    if (!immediate && now - this.lastSeekAt < minInterval) return false;

    this.lastSeekAt = now;
    this.seekStartedAt = now;
    const target = Math.min(Math.max(time, 0), this.videoDuration);

    if (!precise && typeof this.video.fastSeek === 'function') {
      this.video.fastSeek(target);
    } else {
      this.video.currentTime = target;
    }

    return true;
  }

  _recoverStalledSeek(time) {
    if (!(this.video instanceof HTMLMediaElement)) return false;

    const now = performance.now();
    if (now - this.lastSeekRecoveryAt < 1200) {
      this.seekOnlyPendingTime = time;
      this._ensureScrubLoop();
      return false;
    }

    const target = Math.min(Math.max(time, 0), this.videoDuration || time);
    this.lastSeekRecoveryAt = now;
    this.lastSeekAt = 0;
    this.seekStartedAt = 0;
    this.seekOnlyPendingTime = target;
    this.video.pause();
    this.video.playbackRate = 1;

    let resumed = false;
    const resumeSeek = () => {
      if (resumed) return;
      resumed = true;
      if (!(this.video instanceof HTMLMediaElement) || !this.isConnected) return;
      try {
        this.seekStartedAt = performance.now();
        this.lastSeekAt = this.seekStartedAt;
        this.video.currentTime = target;
      } catch (error) {
        this.seekStartedAt = 0;
      }
      this._ensureScrubLoop();
    };

    this.video.addEventListener('loadedmetadata', resumeSeek, { once: true });
    this.video.load();
    if (this.video.readyState >= 1) requestAnimationFrame(resumeSeek);
    this._ensureScrubLoop();
    return true;
  }

  _computeClosestStepIndex(stepRects, anchorY) {
    if (this._cache.isMobile) {
      const activationY = anchorY + Math.min(Math.max(this._getViewportHeight() * 0.08, 28), 72);
      let activeIndex = 0;

      for (let i = 0; i < stepRects.length; i++) {
        if (stepRects[i].top <= activationY) {
          activeIndex = i;
        } else {
          break;
        }
      }

      return activeIndex;
    }

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let i = 0; i < stepRects.length; i++) {
      const rect = stepRects[i];
      const center = rect.top + rect.height / 2;
      const coversAnchor = rect.top <= anchorY && rect.bottom >= anchorY;
      const distance = coversAnchor ? 0 : Math.abs(center - anchorY);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  }

  isMobileView() {
    return this._cache.isMobile || this.mediaQuery.matches;
  }

  _getViewportHeight() {
    return this._cache.isMobile
      ? this.stableViewportHeight || document.documentElement.clientHeight || window.innerHeight
      : window.innerHeight;
  }
}

if (!customElements.get('neuracoil-sticky-story-component')) {
  customElements.define('neuracoil-sticky-story-component', NeuracoilStickyStoryComponent);
}
